import { bpeConfig } from '@enterprise/common';
import {
  checkIsWorkerQualificationType,
  getNotificationState,
  getPersonUid,
  getWorkerQualificationUid
} from 'common/header/notifications/hooks/Notification.utils';
import type { MappedNotification, NotificationMapperOutput } from 'common/header/notifications/Notifications.types';
import { formatDistance } from 'date-fns';
import { Notification } from 'providers/Notifications/Notifications.types';
import { useMemo } from 'react';
import { filterNotEmpty } from 'utils/data/listHelpers';
import { useNotifications } from 'utils/hooks/data/useNotifications';
import { useTenantCode } from 'utils/hooks/useTenantConfig';
import { useNotificationTitle } from './useNotificationTitle';

export const useNotificationMapper = (): NotificationMapperOutput => {
  const {
    notifications,
    isFullyLoaded,
    error,
    markNotificationsAsRead,
    deleteNotifications,
    refetchNotifications,
    loading,
    loadMore
  } = useNotifications();
  const getNotificationTitle = useNotificationTitle();
  const tenantCode = useTenantCode();

  const mappedNotifications = useMemo(
    (): MappedNotification[] =>
      filterNotEmpty(
        notifications.map((notification) => mapToNotification(notification, tenantCode, getNotificationTitle))
      ),
    [getNotificationTitle, notifications, tenantCode]
  );

  const unReadCount = useMemo(
    () => Math.min(mappedNotifications.filter((el) => !el.read).length, 100),
    [mappedNotifications]
  );

  return {
    notifications: mappedNotifications,
    isFullyLoaded,
    error,
    unReadCount,
    markNotificationsAsRead,
    deleteNotifications,
    refetchNotifications,
    loadMore,
    loading
  };
};

const getExportPage = () => `/${bpeConfig.tenant.code}/reports/exports`;

const goToEligibilityProvisioningPage = (personUid: string, workerQualificationUid: string) =>
  `/${bpeConfig.tenant.code}/profile/${personUid}/eligibility-and-provisioning?requirementUid=${workerQualificationUid}`;

const getIconType = ({
  isCancelled,
  isError,
  isReady,
  inProgress,
  isQueued
}: {
  isCancelled: boolean;
  isError: boolean;
  isReady: boolean;
  inProgress: boolean;
  isQueued: boolean;
}): MappedNotification['iconType'] => {
  if (isError) {
    return 'FAILURE';
  }
  if (isCancelled) {
    return 'CANCELLED';
  }

  if (isQueued) {
    return 'IS_QUEUED';
  }

  if (inProgress) {
    return 'IN_PROGRESS';
  }
  return isReady ? 'SUCCESS' : 'INFO';
};

const mapToNotification = (
  notification: Notification,
  tenantCode: string,
  getNotificationTitle: (notificationViewModel: MappedNotification) => {
    title: string;
    description: string;
  }
): MappedNotification | undefined => {
  const { isQueued, inProgress, isCancelled, isError, isReady } = getNotificationState(notification.status);
  const iconType = getIconType({ isQueued, inProgress, isCancelled, isError, isReady });

  const common = {
    ...notification,
    type: notification.type,
    date: formatDistance(new Date(notification.statusModified), new Date()) as string,
    iconType,
    description: '',
    to: undefined,
    title: ''
  };

  if (notification.type?.includes('TASK') || notification.resourceLocation?.includes('api/task')) return undefined;

  switch (notification.type) {
    case 'EXCEL_EXPORT':
    case 'CSV_EXPORT': {
      return {
        ...common,
        ...getNotificationTitle(common),
        to: getExportPage()
      };
    }
    case 'PING':
    case 'WORKDAY_ORG_TREE_SYNC': {
      return {
        ...common,
        ...getNotificationTitle(common)
      };
    }
    case 'GLOBAL_ID_RUN': {
      return {
        ...common,
        ...getNotificationTitle(common),
        to: `/${tenantCode}/settings/tenants/global-id`
      };
    }
    case 'TASK_ASSIGNED': {
      return {
        ...common,
        ...getNotificationTitle(common),
        to: isReady ? `/${tenantCode}/tasks/edit/${notification.resourceUid}` : undefined
      };
    }
    case 'TASK_RESOLVED': {
      return {
        ...common,
        ...getNotificationTitle(common),
        to: isReady ? `/${tenantCode}/tasks/edit/${notification.resourceUid}` : undefined
      };
    }
    case 'IMPORT_UCI': {
      return {
        ...common,
        ...getNotificationTitle(common),
        to: `/${tenantCode}/settings/monitoring/import-logs`
      };
    }
    case 'IMPORT':
    case 'PERSON_EVENT_IMPORT': {
      return {
        ...common,
        ...getNotificationTitle(common),
        to: isReady || isError ? systemImportUrl(notification, tenantCode) : undefined
      };
    }
    case 'USER_IMPORT': {
      return {
        ...common,
        ...getNotificationTitle(common),
        to: isReady || isError ? importUrl(notification, tenantCode) : undefined
      };
    }
    case 'SUPPLIER_RELATIONSHIP_CHANGE': {
      return {
        ...common,
        ...getNotificationTitle(common),
        to: `/${tenantCode}/supplier/${notification.resourceUid}`
      };
    }
    case 'AUDIT_REPORT': {
      if (notification.status !== 'FINISHED_SUCCESS') return undefined;
      return {
        ...common,
        ...getNotificationTitle(common),
        to: `/${tenantCode}/configs/monitoring/audits`
      };
    }
    case 'WORKFLOW_NOTIFICATION': {
      let goTo = notification.resourceUid?.[0] === 'K' ? `/${tenantCode}/tasks/${notification.resourceUid}` : undefined;
      const personUid = getPersonUid(notification);
      const workerQualificationUid = getWorkerQualificationUid(notification);

      if (checkIsWorkerQualificationType(notification) && !!personUid && !!workerQualificationUid) {
        goTo = goToEligibilityProvisioningPage(personUid, workerQualificationUid);
      }
      return {
        ...common,
        ...getNotificationTitle(common),
        // K prefix means its a task resource type... build the url
        // TODO: update with a better backend implementation...
        to: goTo
      };
    }
    default:
      return undefined;
  }
};

const importResourceName = (notification: Notification) =>
  notification.metadata?.find((e) => e.key === 'IMPORTER_ENTITY_TYPE')?.value;

const isInvoiceImportNotification = (notification: Notification) => importResourceName(notification) === 'INVOICE';

const userActivityUrl = (notification: Notification, tenantCode: string) =>
  notification.resourceUid ? `/${tenantCode}/workers?userActivityUid=${notification.resourceUid}` : undefined;

const importUrl = (notification: Notification, tenantCode: string) =>
  isInvoiceImportNotification(notification)
    ? `/${tenantCode}/settings/monitoring/invoiceimports`
    : userActivityUrl(notification, tenantCode);

const genericImportUrl = (notification: Notification, tenantCode: string) =>
  notification.resourceUid ? `/${tenantCode}/configs/monitoring/imports/${notification.resourceUid}` : undefined;

const systemImportUrl = (notification: Notification, tenantCode: string) =>
  isInvoiceImportNotification(notification)
    ? `/${tenantCode}/settings/monitoring/invoiceimports`
    : genericImportUrl(notification, tenantCode);
